import React, { useState, useEffect, useRef, useCallback } from 'react';
import { navigationItems } from '../content/about-content';
import {
  HeroSection,
  WhatIsNeuropedagogy,
  BrainAnatomySection,
  LearningTheorySection,
  PlatformFeaturesSection,
  FivePillarsSection,
  NeuromythsSection,
  SourcesCitations
} from '../components/about';
import '../styles/about-neuropedagogy.css';

/**
 * About Neuropedagogy Page
 *
 * Comprehensive page explaining the neuropedagogy framework behind LRB.
 * Features scroll-spy navigation and interactive infographics.
 */
const AboutNeuropedagogy = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const sectionRefs = useRef({});

  // Set up refs for each section
  const setSectionRef = useCallback((id) => (el) => {
    sectionRefs.current[id] = el;
  }, []);

  // Intersection Observer for scroll-spy
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -50% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all section refs
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsNavOpen(false);
    }
  };

  // Handle keyboard navigation in nav
  const handleNavKeyDown = (e, sectionId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToSection(sectionId);
    }
  };

  // Close nav on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isNavOpen) {
        setIsNavOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isNavOpen]);

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isNavOpen]);

  return (
    <div className="about-neuropedagogy">
      {/* Mobile nav toggle */}
      <button
        className="about-nav__toggle"
        onClick={() => setIsNavOpen(!isNavOpen)}
        aria-label={isNavOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={isNavOpen}
      >
        {isNavOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        )}
      </button>

      {/* Mobile overlay */}
      <div
        className={`about-nav__overlay ${isNavOpen ? 'about-nav__overlay--visible' : ''}`}
        onClick={() => setIsNavOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar navigation */}
      <aside
        className={`about-nav ${isNavOpen ? 'about-nav--open' : ''}`}
        aria-label="Page sections"
      >
        <p className="about-nav__title">Contents</p>
        <nav>
          <ul className="about-nav__list">
            {navigationItems.map((item) => (
              <li key={item.id} className="about-nav__item">
                <a
                  href={`#${item.id}`}
                  className={`about-nav__link ${activeSection === item.id ? 'about-nav__link--active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.id);
                  }}
                  onKeyDown={(e) => handleNavKeyDown(e, item.id)}
                  aria-current={activeSection === item.id ? 'true' : undefined}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <main className="about-content">
        <HeroSection ref={setSectionRef('hero')} id="hero" />
        <WhatIsNeuropedagogy ref={setSectionRef('what-is')} id="what-is" />
        <BrainAnatomySection ref={setSectionRef('brain')} id="brain" />
        <LearningTheorySection ref={setSectionRef('learning')} id="learning" />
        <PlatformFeaturesSection ref={setSectionRef('platform')} id="platform" />
        <FivePillarsSection ref={setSectionRef('pillars')} id="pillars" />
        <NeuromythsSection ref={setSectionRef('myths')} id="myths" />
        <SourcesCitations ref={setSectionRef('sources')} id="sources" />
      </main>
    </div>
  );
};

export default AboutNeuropedagogy;
